import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileText, Trash2, Upload, X, MapPin, Calendar, Users, StickyNote, Pencil, UserPlus } from "lucide-react";
import CollapsibleForm from "../../components/CollapsibleForm";

const CS_MAP = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const wkInitials = (name = "") => {
  const parts = name.trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};
const wkPrettyDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};
import ConfirmDialog from "../../components/ConfirmDialog";
import { useCollapsibleGroups } from "../../lib/useCollapsibleGroups";

export default function WeekendTours({ rows, staffWeekends, apps = [], onSubmitted }) {
  const CS = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
  const REASONS = ["Visa refused", "Participant dropped", "Duplicate row", "Programme changed", "Other"];
  const [f, setF] = useState({ participant_names: "", destinations: "", weekend_trips: "", start_dates: "", end_dates: "", tour_id: "", tour_date: "", notes: "" });
  const [tours, setTours] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const [upResult, setUpResult] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, priced, errors, file }
  const [confirming, setConfirming] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Bulk-delete state — one-tap "delete all my staff weekend bookings"
  // with the same reason-picker modal used on program bookings so the
  // Founder gets the same cancellation-trend signal.
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkReasonNote, setBulkReasonNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const doBulkDeleteWeekends = async () => {
    setBulkBusy(true);
    const reasonText = bulkReason === "Other" ? (bulkReasonNote || "Other").trim() : bulkReason;
    try {
      const { data } = await api.post("/partner-portal/staff-weekend-bookings/bulk-delete", {
        ids: (staffWeekends || []).map(w => w.id),
        reason: reasonText || "",
      });
      toast.success(`Removed ${data.deleted} weekend booking${data.deleted === 1 ? "" : "s"} — Recycle-Bin backed.`);
      setBulkDelOpen(false); setBulkReason(""); setBulkReasonNote("");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };
  const { collapsed: collapsedMonths, toggle: toggleMonth } = useCollapsibleGroups("partner-weekend-tours");

  // Load available weekend tours the partner can pick from — wrapped in
  // useCallback so listing it as a useEffect dep doesn't cause the linter
  // to complain, and so a parent can trigger a refresh via `onSubmitted`
  // without recreating the fetch on every render (which would cause an
  // infinite loop).
  const loadTours = useCallback(async () => {
    try { const { data } = await api.get("/weekend-tours"); setTours(data || []); }
    catch (err) { console.debug("weekend tours load failed:", err?.message); }
  }, []);
  useEffect(() => { loadTours(); }, [loadTours]);

  const selectedTour = tours.find(t => t.id === f.tour_id);

  const pickTour = (tid) => {
    const t = tours.find(x => x.id === tid);
    setF(prev => ({
      ...prev,
      tour_id: tid,
      destinations: t ? `${t.city}, ${t.country}` : prev.destinations,
      weekend_trips: t ? t.title : prev.weekend_trips,
    }));
  };

  const editRow = (r) => {
    // Rehydrate the submit form above with this pending row so partners can
    // tweak dates / participants / notes without deleting + re-creating.  The
    // POST endpoint always creates a fresh row, so we also remove the old one
    // in the same flow — but only AFTER the user re-submits (avoids losing
    // data if they navigate away).
    setF({
      participant_names: r.participant_names || "",
      destinations: r.destinations || "",
      weekend_trips: r.weekend_trips || r.tour_title || "",
      start_dates: r.start_dates || "",
      end_dates: r.end_dates || "",
      tour_id: r.tour_id || "",
      tour_date: r.tour_date || "",
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setTimeout(() => document.querySelector('[data-testid="pw-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setF({ participant_names: "", destinations: "", weekend_trips: "", start_dates: "", end_dates: "", tour_id: "", tour_date: "", notes: "" });
  };

  const submit = async () => {
    if (!f.participant_names) return toast.error("Participant names required");
    if (!f.tour_id && !f.destinations) return toast.error("Pick a weekend tour or type a destination");
    setBusy(true);
    try {
      await api.post("/partner-weekend-bookings", f);
      // If we were editing an existing pending row, delete the old one so the
      // partner doesn't end up with two copies.  Silent failure — the create
      // already succeeded so we keep the UX optimistic.
      if (editingId) {
        try { await api.delete(`/partner-portal/weekend-bookings/${editingId}`); } catch (err) { console.debug("edit delete old failed:", err?.message); }
        setEditingId(null);
      }
      toast.success(editingId ? "Weekend booking updated" : "Sent for approval");
      setF({ participant_names: "", destinations: "", weekend_trips: "", start_dates: "", end_dates: "", tour_id: "", tour_date: "", notes: "" });
      onSubmitted?.();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/partner-portal/weekend-bookings/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "aii-weekend-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error("Could not download template"); }
  };
  const uploadFile = async (file, { commit = false } = {}) => {
    if (!file) return;
    setBusy(true); setUpResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const url = commit
        ? "/partner-portal/weekend-bookings/upload"
        : "/partner-portal/weekend-bookings/upload?dry_run=true";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!commit) {
        // Show the preview modal — partners eyeball prices before committing.
        setPreview({ ...data, file });
      } else {
        setUpResult(data);
        toast.success(`${data.created} weekend bookings uploaded${data.priced ? ` · ${data.priced} auto-priced` : ""}`);
        setPreview(null);
        onSubmitted?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="aii-card p-4 border-l-4 border-brand" data-testid="wkbulk-uploader">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold flex items-center gap-1.5"><Upload size={14} className="text-brand"/> Bulk upload weekend bookings</div>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1">Rows land directly in the team&apos;s Weekend Bookings — attributed to your account. The team can pre-price via Weekend Tours.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadTemplate} data-testid="wkbulk-template" className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><FileText size={12}/> Download template</button>
            <button onClick={() => ref.current?.click()} disabled={busy} data-testid="wkbulk-upload" className="text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1 disabled:opacity-50"><Upload size={12}/> {busy ? "Uploading…" : "Upload Excel"}</button>
            <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => uploadFile(e.target.files?.[0])}/>
          </div>
        </div>
        {upResult && <div className="mt-2 text-xs bg-[color:var(--aii-green-tint)] rounded p-2"><b>{upResult.created}</b> uploaded{upResult.errors?.length > 0 ? ` · ${upResult.errors.length} errors` : ""}</div>}
      </div>

      <CollapsibleForm
        title={editingId ? "Edit weekend tour request" : "Submit a weekend tour request"}
        hint="Participant names, tour choice, dates. Founder reviews before it turns into a confirmed booking."
        testid="pw-form"
        icon={MapPin}
        defaultOpen={!!editingId}
      >
        {editingId && (
          <div className="flex items-center justify-end">
            <button onClick={cancelEdit} data-testid="pw-cancel-edit" className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50 flex items-center gap-1">
              <X size={11}/> Cancel edit
            </button>
          </div>
        )}
        <input className="input-aii" placeholder="Participant names" value={f.participant_names} onChange={e => setF({ ...f, participant_names: e.target.value })} data-testid="pw-names"/>
        {(() => {
          // Dropdown of the partner's approved participants for the selected
          // tour's destination. Picking a name appends it to the free-text
          // Participant names field (comma-separated) so the partner can mix
          // multiple candidates without re-typing every name.
          const tour = tours.find(x => x.id === f.tour_id);
          const dstCountry = (tour?.country || "").toLowerCase().trim();
          const dstCity = (tour?.city || "").toLowerCase().trim();
          const alreadyChosen = new Set(
            (f.participant_names || "").split(/[,\n]/).map(s => s.trim().toLowerCase()).filter(Boolean)
          );
          // Approved participants only — you can't book a weekend for someone
          // who hasn't been approved for a programme yet.
          const candidates = (apps || []).filter(a => (a.status || "").toLowerCase() === "approved");
          const scoped = candidates.filter(a => {
            if (!dstCountry && !dstCity) return true; // no tour picked = all approved
            const c = (a.destination || a.country || "").toLowerCase().trim();
            const ci = (a.city || "").toLowerCase().trim();
            return (!dstCountry || c === dstCountry) && (!dstCity || ci === dstCity || !ci);
          }).filter(a => !alreadyChosen.has((a.name || a.customer || "").toLowerCase().trim()));
          if (!scoped.length) return null;
          const addName = (nm) => {
            if (!nm) return;
            const parts = (f.participant_names || "").split(",").map(s => s.trim()).filter(Boolean);
            if (parts.some(p => p.toLowerCase() === nm.toLowerCase())) return;
            parts.push(nm);
            setF(prev => ({ ...prev, participant_names: parts.join(", ") }));
          };
          return (
            <div className="border border-[color:var(--aii-border)] rounded-md p-2 bg-[color:var(--aii-green-tint)]/30" data-testid="pw-participant-picker">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1 mb-1"><UserPlus size={11}/> Add from your approved participants
                {tour && <span className="text-[9px] font-normal normal-case text-[color:var(--aii-muted)] tracking-normal ml-1">— {tour.city}, {tour.country}</span>}
              </div>
              <select
                data-testid="pw-participant-select"
                value=""
                onChange={(e) => { addName(e.target.value); e.target.value = ""; }}
                className="input-aii text-xs">
                <option value="">— Pick a participant to add —</option>
                {scoped.map(a => {
                  const nm = a.name || a.customer || "";
                  return (
                    <option key={a.id} value={nm} data-testid={`pw-participant-opt-${a.id}`}>
                      {nm}{a.program ? ` · ${a.program}` : ""}{a.start_date ? ` · from ${a.start_date}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          );
        })()}
        {tours.length > 0 && (
          <select data-testid="pw-tour" className="input-aii" value={f.tour_id} onChange={e => pickTour(e.target.value)}>
            <option value="">— Choose a weekend tour curated by our team —</option>
            {tours.map(t => (
              <option key={t.id} value={t.id}>
                {t.title} · {t.city}, {t.country} · {t.duration_days}d · {CS[t.currency] || t.currency}{Number(t.price || 0).toLocaleString()}
              </option>
            ))}
          </select>
        )}
        {selectedTour && (
          <div className="text-xs bg-[color:var(--aii-green-tint)] border border-brand rounded p-2 flex items-start justify-between gap-2" data-testid="pw-tour-summary">
            <div>
              <div className="font-semibold text-brand">{selectedTour.title}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">
                {selectedTour.city}, {selectedTour.country} · {selectedTour.duration_days} day{selectedTour.duration_days === 1 ? "" : "s"} · Min PAX {selectedTour.min_pax || 1}
              </div>
              {selectedTour.description && <div className="text-[11px] mt-1">{selectedTour.description}</div>}
            </div>
            <span className="font-display font-bold text-lg text-brand whitespace-nowrap">
              {CS[selectedTour.currency] || selectedTour.currency}{Number(selectedTour.price || 0).toLocaleString()}
            </span>
          </div>
        )}
        <input className="input-aii" placeholder="Destinations (auto-filled if tour picked)" value={f.destinations} onChange={e => setF({ ...f, destinations: e.target.value })}/>
        <input className="input-aii" placeholder="Weekend trip name (auto-filled if tour picked)" value={f.weekend_trips} onChange={e => setF({ ...f, weekend_trips: e.target.value })}/>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Tour date
            <input className="input-aii mt-1" type="date" value={f.tour_date} onChange={e => setF({ ...f, tour_date: e.target.value })} data-testid="pw-tour-date"/>
          </label>
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Start date
            <input className="input-aii mt-1" type="date" value={f.start_dates} onChange={e => setF({ ...f, start_dates: e.target.value })}/>
          </label>
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">End date
            <input className="input-aii mt-1" type="date" value={f.end_dates} onChange={e => setF({ ...f, end_dates: e.target.value })}/>
          </label>
        </div>
        <textarea className="input-aii" rows={2} placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/>
        <button onClick={submit} disabled={busy} data-testid="pw-submit" className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">{busy ? "Sending…" : (editingId ? "Update request" : "Submit for approval")}</button>
      </CollapsibleForm>
      {rows.length === 0 && (staffWeekends || []).length === 0 && (
        <div className="aii-card p-6 text-center" data-testid="pw-empty">
          <h3 className="font-semibold text-sm mb-1">My weekend tour requests</h3>
          <p className="text-xs text-[color:var(--aii-muted)] leading-relaxed">
            No weekend tours yet — new requests you submit above (or bulk-upload) will appear here,<br/>
            grouped by tour month so you can fold months you aren&apos;t running.
          </p>
        </div>
      )}
      {(staffWeekends || []).length > 0 && (
        <div className="flex items-center justify-end pt-1">
          <button
            data-testid="wk-bulk-delete-all"
            onClick={() => { setBulkReason(""); setBulkReasonNote(""); setBulkDelOpen(true); }}
            className="text-[10px] px-2 py-1 border border-red-400 text-red-700 rounded hover:bg-red-50 font-semibold uppercase flex items-center gap-1">
            <Trash2 size={10}/> Delete all {(staffWeekends || []).length} team weekend booking{(staffWeekends || []).length === 1 ? "" : "s"}
          </button>
        </div>
      )}
      {(rows.length > 0 || (staffWeekends || []).length > 0) && (() => {
        // Dedup: after Iter 58.40 an approved `partner_weekend_pending` row
        // spawns a linked `weekend_bookings` doc AND now also matches the
        // `staff-weekend-bookings` query (because `partner_id` is set on it).
        // Show the partner-request card as the source-of-truth; drop the
        // staff-booking twin whose id equals any request's `linked_booking_id`.
        const linkedIds = new Set(
          rows.map(r => r.linked_booking_id).filter(Boolean)
        );
        const staffFiltered = (staffWeekends || []).filter(w => !linkedIds.has(w.id));
        // Unify partner-submitted requests + staff-created bookings, then
        // Cluster by exact tour-date (YYYY-MM-DD) so partners running
        // multiple weekends on the same day see them side by side. Falls
        // back to start_dates when the founder hasn't stamped a tour_date.
        const items = [
          ...rows.map(r => ({ kind: "row", item: r, date: r.tour_date || r.start_dates || "" })),
          ...staffFiltered.map(w => ({ kind: "staff", item: w, date: w.tour_date || w.start_dates || "" })),
        ];
        const groups = new Map();
        for (const it of items) {
          const k = (it.date || "").slice(0, 10) || "No date";
          if (!groups.has(k)) groups.set(k, []);
          groups.get(k).push(it);
        }
        const prettyDate = (iso) => {
          if (!iso || iso === "No date") return "No date";
          const d = new Date(`${iso}T00:00:00`);
          if (Number.isNaN(d.getTime())) return iso;
          return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
        };
        const renderRow = (r) => {
          const statusCls = r.status === "approved"
            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : r.status === "rejected"
              ? "bg-red-100 text-red-800 border-red-200"
              : "bg-amber-100 text-amber-800 border-amber-200";
          const canEdit = r.status !== "approved" && r.status !== "rejected"; // only pending rows are editable
          return (
            <div key={r.id} data-testid={`pw-row-${r.id}`}
                 className="aii-card p-4 text-sm hover:border-brand hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[color:var(--aii-green-tint)] text-brand font-display font-bold text-sm flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    {wkInitials(r.participant_names)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-base truncate">{r.participant_names || "—"}</div>
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border ${statusCls}`}>{r.status}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border bg-emerald-100 text-emerald-800 border-emerald-200">Partner</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.tour_price > 0 && (
                    <span data-testid={`pw-price-${r.id}`} className="text-base font-display font-semibold text-brand whitespace-nowrap">
                      {CS_MAP[r.tour_currency] || r.tour_currency || "$"}{Number(r.tour_price || 0).toLocaleString()}
                    </span>
                  )}
                  {canEdit && (
                    <button
                      data-testid={`pw-edit-${r.id}`}
                      onClick={() => editRow(r)}
                      title="Edit this weekend request"
                      className="text-[color:var(--aii-muted)] hover:text-brand p-1.5 rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1 text-xs font-semibold">
                      <Pencil size={13}/> Edit
                    </button>
                  )}
                  <button
                    data-testid={`pw-delete-${r.id}`}
                    onClick={() => setConfirming({
                      id: r.id, names: r.participant_names,
                      approved: r.status === "approved",
                      price: r.tour_price || 0, currency: r.tour_currency || "USD",
                      action: async () => {
                        try {
                          await api.delete(`/partner-portal/weekend-bookings/${r.id}`);
                          toast.success("Weekend booking deleted");
                          onSubmitted?.();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
                      },
                    })}
                    title={r.status === "approved" ? "Delete approved weekend booking (also removes linked booking + sale)" : "Delete request"}
                    className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 rounded hover:bg-red-50 flex items-center gap-1 text-xs font-semibold">
                    <Trash2 size={13}/> Delete
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-sky-50/40">
                  <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold flex items-center gap-1 mb-1"><MapPin size={10}/> Tour</div>
                  <div className="text-sm font-semibold truncate" title={r.tour_title || r.weekend_trips || "—"}>{r.tour_title || r.weekend_trips || "—"}</div>
                  {r.destinations && (
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 truncate" title={r.destinations}>{r.destinations}</div>
                  )}
                </div>
                <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-emerald-50/40">
                  <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1 mb-1"><Calendar size={10}/> Tour date</div>
                  <div className="text-sm font-semibold">{wkPrettyDate(r.tour_date)}</div>
                  {(r.start_dates || r.end_dates) && (
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                      {r.start_dates ? wkPrettyDate(r.start_dates) : "—"}
                      {r.end_dates ? ` → ${wkPrettyDate(r.end_dates)}` : ""}
                    </div>
                  )}
                </div>
                <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-amber-50/40">
                  <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1 mb-1"><Users size={10}/> Participants</div>
                  <div className="text-sm font-semibold">{(r.participant_names || "").split(/[,\n]/).filter(Boolean).length || 1}</div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">Requested {r.created_at ? wkPrettyDate((r.created_at || "").slice(0, 10)) : "—"}</div>
                </div>
              </div>

              {r.notes && (
                <div className="mt-2 text-[11px] text-[color:var(--aii-text-2)] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded px-2 py-1.5 flex items-start gap-1.5">
                  <StickyNote size={11} className="mt-0.5 text-[color:var(--aii-muted)] flex-shrink-0"/>
                  <span className="whitespace-pre-wrap break-words">{r.notes}</span>
                </div>
              )}
            </div>
          );
        };
        const renderStaff = (w) => (
          <div key={`swk-${w.id}`} data-testid={`staff-wk-row-${w.id}`}
               className="aii-card p-4 text-sm hover:border-brand hover:shadow-sm transition">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-display font-bold text-sm flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  {wkInitials(w.customer)}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-base truncate">{w.customer || "—"}</div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border bg-emerald-100 text-emerald-800 border-emerald-200">Booked</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border bg-slate-100 text-slate-700 border-slate-200">Team</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {w.tour_price > 0 && (
                  <span data-testid={`staff-wk-price-${w.id}`} className="text-base font-display font-semibold text-brand whitespace-nowrap">
                    {CS_MAP[w.tour_currency] || w.tour_currency || "$"}{Number(w.tour_price || 0).toLocaleString()}
                  </span>
                )}
                <button
                  data-testid={`staff-wk-delete-${w.id}`}
                  onClick={() => setConfirming({
                    id: w.id, names: w.customer, approved: true,
                    price: w.tour_price || 0, currency: w.tour_currency || "USD",
                    action: async () => {
                      try {
                        await api.delete(`/partner-portal/staff-weekend-bookings/${w.id}`);
                        toast.success("Weekend booking deleted");
                        onSubmitted?.();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
                    },
                  })}
                  title="Delete this weekend booking (also removes its price from your Amount to pay)"
                  className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 rounded hover:bg-red-50 flex items-center gap-1 text-xs font-semibold">
                  <Trash2 size={13}/> Delete
                </button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-sky-50/40">
                <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold flex items-center gap-1 mb-1"><MapPin size={10}/> Tour</div>
                <div className="text-sm font-semibold truncate" title={w.tour_title || w.destinations || w.weekend_trips || "—"}>{w.tour_title || w.destinations || w.weekend_trips || "—"}</div>
                {w.destinations && w.destinations !== w.tour_title && (
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 truncate" title={w.destinations}>{w.destinations}</div>
                )}
              </div>
              <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-emerald-50/40">
                <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1 mb-1"><Calendar size={10}/> Tour date</div>
                <div className="text-sm font-semibold">{wkPrettyDate(w.tour_date || w.start_dates)}</div>
                {w.end_dates && (
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">→ {wkPrettyDate(w.end_dates)}</div>
                )}
              </div>
              <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-amber-50/40">
                <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1 mb-1"><Users size={10}/> Booked by</div>
                <div className="text-sm font-semibold truncate">Our team</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">On your behalf</div>
              </div>
            </div>

            {w.notes && (
              <div className="mt-2 text-[11px] text-[color:var(--aii-text-2)] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded px-2 py-1.5 flex items-start gap-1.5">
                <StickyNote size={11} className="mt-0.5 text-[color:var(--aii-muted)] flex-shrink-0"/>
                <span className="whitespace-pre-wrap break-words">{w.notes}</span>
              </div>
            )}
          </div>
        );
        return (
          <div>
            <h3 className="font-semibold mb-2">My weekend tour requests</h3>
            <div className="space-y-3">
              {[...groups.entries()].sort(([a],[b]) => a.localeCompare(b)).map(([ym, list]) => {
                const isCollapsed = collapsedMonths.has(ym);
                return (
                  <div key={`wk-ym-${ym}`} data-testid={`pw-date-group-${ym}`}>
                    <button type="button" data-testid={`pw-date-hdr-${ym}`}
                      onClick={() => toggleMonth(ym)}
                      className="w-full text-left bg-[color:var(--aii-green-tint)]/60 hover:bg-[color:var(--aii-green-tint)] px-3 py-1.5 rounded text-[11px] uppercase tracking-widest font-bold text-brand select-none">
                      {isCollapsed ? "▸" : "▾"} {prettyDate(ym)} · {list.length} tour{list.length === 1 ? "" : "s"}
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-2 mt-2">
                        {list.map(it => it.kind === "row" ? renderRow(it.item) : renderStaff(it.item))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      <ConfirmDialog
        open={!!confirming}
        title={`Delete weekend booking for ${confirming?.names || ""}?`}
        description={confirming?.approved
          ? `This also removes the linked weekend booking record and its sale.${confirming?.price > 0 ? `\nThis will remove ${CS[confirming?.currency] || "$"}${Number(confirming.price).toLocaleString()} from your Amount to pay.` : ""}\nThis action cannot be undone.`
          : "The weekend tour request will be removed permanently.\nThis action cannot be undone."}
        confirmLabel="Delete booking"
        onConfirm={() => { confirming?.action(); setConfirming(null); }}
        onCancel={() => setConfirming(null)}
        testid="pw-delete-confirm"
      />

      {preview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="wk-preview-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Upload size={16} className="text-brand"/> Preview weekend upload</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Rows are matched against the team&apos;s weekend tours by title. Review the prices before committing.</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                  <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold">{preview.created} rows</span>
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">{preview.priced} auto-priced</span>
                  {preview.errors?.length > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold">{preview.errors.length} errors</span>}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {(preview.previews || []).length === 0 && <div className="p-8 text-center text-sm text-[color:var(--aii-muted)] italic">No rows parsed.</div>}
              {(preview.previews || []).length > 0 && (
                <table className="w-full text-xs" data-testid="wk-preview-table">
                  <thead className="bg-[color:var(--aii-bg)] sticky top-0"><tr className="text-left">
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Tour</th>
                    <th className="px-3 py-2">Start</th>
                    <th className="px-3 py-2">Match</th>
                    <th className="px-3 py-2 text-right">Price</th>
                  </tr></thead>
                  <tbody>
                    {preview.previews.map((r, i) => (
                      <tr key={`wp-${i}`} className="border-t border-[color:var(--aii-border)]/50">
                        <td className="px-3 py-1.5 text-[color:var(--aii-muted)]">{r.row}</td>
                        <td className="px-3 py-1.5 font-medium">{r.customer}</td>
                        <td className="px-3 py-1.5 truncate max-w-[12rem]">{r.tour_title}</td>
                        <td className="px-3 py-1.5 text-[color:var(--aii-muted)] whitespace-nowrap">{r.start || "—"}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.source === "weekend-tour-title" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{r.source}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap">
                          {r.price > 0 ? <span className="text-brand">{CS[r.currency] || r.currency}{Number(r.price).toLocaleString()}</span> : <span className="text-[color:var(--aii-muted)] font-normal">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(preview.errors || []).length > 0 && (
                <div className="p-3 border-t border-[color:var(--aii-border)] bg-red-50">
                  <div className="text-[11px] uppercase font-bold text-red-800 mb-1">Row errors</div>
                  <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                    {preview.errors.map((e, i) => <li key={`we-${i}`}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button data-testid="wk-preview-cancel" onClick={() => setPreview(null)} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="wk-preview-apply" disabled={busy || preview.created === 0}
                      onClick={() => uploadFile(preview.file, { commit: true })}
                      className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                <Upload size={13}/> {busy ? "Uploading…" : `Apply upload · ${preview.created} row${preview.created === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {bulkDelOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !bulkBusy && setBulkDelOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="wk-bulk-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)]">
              <h3 className="font-display text-lg font-semibold">Delete every weekend booking?</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">
                Nukes all {(staffWeekends || []).length} team-booked weekend rows on your account. Snapshotted to the Recycle Bin — your Aii coordinator can restore.
              </p>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Why?</div>
              {REASONS.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="wk-reason" data-testid={`wk-reason-${r.split(" ")[0].toLowerCase()}`}
                         checked={bulkReason === r} onChange={() => setBulkReason(r)}/>
                  {r}
                </label>
              ))}
              {bulkReason === "Other" && (
                <input data-testid="wk-reason-note" value={bulkReasonNote} onChange={e => setBulkReasonNote(e.target.value)}
                       placeholder="Tell your coordinator what happened…" autoFocus
                       className="w-full mt-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-between gap-2">
              <button onClick={() => setBulkDelOpen(false)} disabled={bulkBusy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="wk-bulk-submit"
                onClick={doBulkDeleteWeekends}
                disabled={bulkBusy}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <Trash2 size={13}/> {bulkBusy ? "Deleting…" : `Delete ${(staffWeekends || []).length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
